/* eslint-disable no-useless-return */
/* eslint-disable no-unused-expressions */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Spin, Tooltip, Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { HttpClient } from '../../utils/HttpClient';

const GOOGLE_AUTH_WINDOW_KEY = 'google-login-window';

type ButtonContainerProps = {
    children: any;
    googleUrlError: boolean;
};

const ButtonContainer = ({ children, googleUrlError }: ButtonContainerProps) => {
    return googleUrlError ? (
        <Tooltip
            data-testid="tooltipError"
            title="An error occured while fetching google auth url."
            placement="bottom"
            color="#f88379"
        >
            {children}
        </Tooltip>
    ) : (
        <div>{children}</div>
    );
};

export type GoogleAuthProps = {
    googleAuth: (...params: any) => void;
};

const GoogleAuth: FunctionComponent<GoogleAuthProps> = ({ googleAuth }) => {
    const [googleAuthWindow, setGoogleAuthWindow] = useState<Window | null>();
    const [googleUrl, setGoogleUrl] = useState<string>();
    const [googleUrlError, setGoogleUrlError] = useState<boolean>(false);
    const [isMounted, setIsMounted] = useState<boolean>(false);

    const fetchGoogleLoginUrl = async (): Promise<void> => {
        try {
            const httpResponse = await HttpClient.get(`/google/login-url`);
            setGoogleUrl(httpResponse.data.message);
        } catch (error) {
            setGoogleUrlError(true);
        }
    };

    useEffect(() => {
        if (!googleUrl && isMounted) fetchGoogleLoginUrl();
    }, [googleUrl, isMounted]);

    // Clean up callback approach...
    useEffect(() => {
        setIsMounted(true);
        return () => {
            setIsMounted(false);
        };
    }, []);

    const checkGoogleWindowUrl = async (event: MessageEvent) => {
        const { origin, source } = event;
        if (origin !== window.location.origin || (source && (source as Window).name !== GOOGLE_AUTH_WINDOW_KEY)) {
            return;
        }

        const { data: search } = event;

        const params = new URLSearchParams(search);

        const googleCode = params.get('code');

        setGoogleAuthWindow(null);

        await googleAuth(googleCode);
    };

    useEffect(() => {
        if (googleAuthWindow) {
            window.addEventListener('message', checkGoogleWindowUrl, false);
        }

        return () => window.removeEventListener('message', checkGoogleWindowUrl, false);
    }, [googleAuthWindow]);

    useEffect(() => {
        const { search, origin } = window.location;
        if (window.opener) {
            // send them to the opening window
            window.opener.postMessage(search, origin);
            // close the popup
            if (!window.menubar.visible) window.close();
        }
    }, []);

    const openGoogleAuthWindow = (): void => {
        const windowRef = window.open(
            googleUrl,
            GOOGLE_AUTH_WINDOW_KEY,
            `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no`,
        );
        setGoogleAuthWindow(windowRef);
    };

    const handleClick = (): void => {
        if (googleUrl) {
            !googleAuthWindow ? openGoogleAuthWindow() : googleAuthWindow.focus();
        }
    };

    return (
        <div
            style={{
                width: '100%',
            }}
        >
            <ButtonContainer googleUrlError={googleUrlError}>
                <Button
                    style={{ width: '100%' }}
                    data-testid="button"
                    disabled={!googleUrl || googleUrlError}
                    onClick={(): void => handleClick()}
                >
                    {googleUrl || googleUrlError ? (
                        <img
                            data-testid="googleIcon"
                            style={{ width: '18px', height: '18px', marginRight: '1em' }}
                            alt="google logo"
                            src="/assets/images/google-logo.svg"
                        />
                    ) : (
                        <Spin
                            data-testid="loadingSpin"
                            indicator={<LoadingOutlined style={{ fontSize: 18, marginRight: '1em' }} spin />}
                        />
                    )}
                    <span>Continue with Google</span>
                </Button>
            </ButtonContainer>
        </div>
    );
};

export default GoogleAuth;
